import { Button } from '@/components/ui/button'
import { Icon, ICONS } from '@/lib/icons'
import type { Player } from '@/types/api'
import { motion } from 'framer-motion'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

interface PodiumExtraStats {
  setDifference?: number
  eloRating?: number
  ladderPosition?: number
}

interface PodiumEntry {
  players: Player[]
  matchesPlayed: number
  wins: number
  losses: number
  winRate?: number
  extraStats?: PodiumExtraStats
}

interface TournamentPodiumProps {
  champion: PodiumEntry
  runnerUp: PodiumEntry
  thirdPlace?: PodiumEntry
  seriesTitle?: string
  completedAt?: string
}

export function TournamentPodium({
  champion,
  runnerUp,
  thirdPlace,
  seriesTitle,
  completedAt,
}: TournamentPodiumProps) {
  const { t } = useTranslation()
  const [showConfetti, setShowConfetti] = useState(false)
  const hasCelebratedRef = useRef(false)

  useEffect(() => {
    if (hasCelebratedRef.current) {
      return
    }
    hasCelebratedRef.current = true
    setShowConfetti(true)
    const timer = window.setTimeout(() => {
      setShowConfetti(false)
    }, 2600)
    return () => window.clearTimeout(timer)
  }, [])

  const confettiPieces = useMemo(() => {
    const colors = ['#facc15', '#f97316', '#38bdf8', '#34d399', '#fb7185', '#c084fc']
    return Array.from({ length: 80 }, (_, index) => ({
      id: index,
      left: `${(index / 80) * 100}%`,
      delay: (index % 16) * 0.08,
      duration: 1.8 + (index % 5) * 0.1,
      background: colors[index % colors.length],
    }))
  }, [])

  const shareTournament = useCallback(async () => {
    if (typeof window === 'undefined') {
      toast.error(t('knockout.podium.shareError', 'Unable to share tournament results right now.'))
      return
    }

    const shareLabel = seriesTitle?.trim().length
      ? t('knockout.podium.shareTitle', { series: seriesTitle })
      : t('knockout.podium.shareTitle', { series: t('knockout.podium.title', 'Tournament Results') })

    const championNames = formatPlayerNames(champion.players)
    const runnerUpNames = formatPlayerNames(runnerUp.players)
    const thirdPlaceNames = thirdPlace ? formatPlayerNames(thirdPlace.players) : undefined

    const shareMessage = thirdPlaceNames
      ? t('knockout.podium.shareTextWithBronze', {
          champion: championNames,
          runnerUp: runnerUpNames,
          thirdPlace: thirdPlaceNames,
        })
      : t('knockout.podium.shareText', {
          champion: championNames,
          runnerUp: runnerUpNames,
        })

    const shareUrl = window.location.href
    const nav = window.navigator

    try {
      if (nav?.share) {
        await nav.share({ title: shareLabel, text: shareMessage, url: shareUrl })
        toast.success(t('knockout.podium.shareSuccess', 'Shared podium results'))
        return
      }

      if (nav?.clipboard?.writeText) {
        await nav.clipboard.writeText(`${shareLabel}\n${shareMessage}\n${shareUrl}`)
        toast.success(t('knockout.podium.shareClipboardSuccess', 'Link copied to clipboard'))
        return
      }

      toast.error(t('knockout.podium.shareClipboardError', 'Copy to clipboard is not supported in this browser.'))
    } catch (error: unknown) {
      if ((error as DOMException)?.name === 'AbortError') {
        return
      }
      toast.error(t('knockout.podium.shareError', 'Unable to share tournament results right now.'))
    }
  }, [champion.players, runnerUp.players, seriesTitle, thirdPlace, t])

  const podiumWrapperClass = thirdPlace ? 'md:grid-cols-3' : 'md:grid-cols-[repeat(2,minmax(0,1fr))]'

  return (
    <div
      data-testid="tournament-podium"
      className="relative overflow-hidden rounded-3xl border border-primary/10 bg-gradient-to-br from-amber-50 via-white to-amber-100 p-6 shadow-xl dark:from-amber-500/10 dark:via-slate-950 dark:to-slate-900"
    >
      {showConfetti && (
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          {confettiPieces.map(piece => (
            <motion.span
              key={piece.id}
              className="absolute h-3 w-1 rounded-full"
              style={{ left: piece.left, backgroundColor: piece.background }}
              initial={{ opacity: 0, y: -20, rotate: 0 }}
              animate={{ opacity: [0, 1, 1, 0], y: [-20, 200, 260], rotate: [0, 120, 240] }}
              transition={{ duration: piece.duration, delay: piece.delay, ease: 'easeOut' }}
            />
          ))}
        </div>
      )}

      <div className="relative z-10 space-y-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-3xl font-bold tracking-tight text-foreground">
              {t('knockout.podium.title', 'Tournament Results')}
            </h2>
            <p className="text-sm text-muted-foreground">
              {t('knockout.podium.subtitle', 'Final standings and performance highlights')}
            </p>
          </div>
          <Button variant="secondary" size="sm" className="w-full sm:w-auto" onClick={shareTournament}>
            <Icon icon={ICONS.export} width={16} height={16} />
            {t('knockout.podium.share', 'Share results')}
          </Button>
        </div>

        <div className={`grid items-end gap-6 ${podiumWrapperClass}`}>
          <PodiumColumn
            position={2}
            title={t('knockout.podium.runnerUp', 'Runner-up')}
            data={runnerUp}
            accent="bg-gradient-to-b from-gray-200 to-gray-300 dark:from-slate-700 dark:to-slate-800"
            highlight={false}
          />

          <PodiumColumn
            position={1}
            title={t('knockout.podium.champion', 'Champion')}
            data={champion}
            accent="bg-gradient-to-b from-yellow-300 to-yellow-500 dark:from-yellow-600 dark:to-yellow-700"
            highlight
            showWinRate
          />

          {thirdPlace && (
            <PodiumColumn
              position={3}
              title={t('knockout.podium.thirdPlace', 'Third place')}
              data={thirdPlace}
              accent="bg-gradient-to-b from-amber-500 to-amber-600 dark:from-amber-700 dark:to-amber-800"
              highlight={false}
            />
          )}
        </div>
        {completedAt && (
          <div className="text-xs text-muted-foreground">
            {t('knockout.podium.completedAt', 'Completed at {{date}}', {
              date: new Date(completedAt).toLocaleString(),
            })}
          </div>
        )}
      </div>
    </div>
  )
}

interface PodiumColumnProps {
  position: 1 | 2 | 3
  title: string
  data: PodiumEntry
  accent: string
  highlight: boolean
  showWinRate?: boolean
}

function PodiumColumn({ position, title, data, accent, highlight, showWinRate }: PodiumColumnProps) {
  const { t } = useTranslation()
  const displayNames = formatPlayerNames(data.players)
  const isSharedPlacement = data.players.length > 1

  return (
    <div
      data-testid={`podium-column-${position}`}
      className={`flex flex-col items-center gap-5 ${highlight ? 'md:-translate-y-6' : ''}`}
    >
      <div className="flex flex-col items-center text-center">
        <div
          className={`flex h-20 w-20 items-center justify-center rounded-full border-4 border-white shadow-lg dark:border-muted ${highlight ? 'bg-warning text-warning-foreground' : 'bg-muted text-muted-foreground'}`}
        >
          <Icon icon={highlight ? ICONS.trophy : ICONS.user} width={36} height={36} />
        </div>
        <div className="mt-3 text-lg font-semibold text-foreground">{displayNames}</div>
        <div className="text-sm text-muted-foreground">
          {isSharedPlacement
            ? t('knockout.podium.sharedPlacement', {
                title,
                count: data.players.length,
              })
            : title}
        </div>
      </div>

      <div className={`w-full max-w-xs rounded-2xl border border-white/40 bg-background/90 p-5 shadow-lg ring-1 ring-black/5 dark:border-white/5 dark:bg-muted/80 ${highlight ? 'ring-warning/40 dark:ring-warning/50' : ''}`}>
        <div className={`${accent} flex h-16 items-center justify-center rounded-xl text-3xl font-bold text-white shadow-inner`}>
          {position}
        </div>
        <dl className="mt-4 space-y-3 text-sm">
          <div className="flex items-center justify-between">
            <dt className="text-muted-foreground uppercase tracking-wide">
              {t('knockout.podium.matchesPlayed', 'Matches played')}
            </dt>
            <dd className="font-semibold text-foreground">{data.matchesPlayed}</dd>
          </div>
          <div className="flex items-center justify-between">
            <dt className="text-muted-foreground uppercase tracking-wide">
              {t('knockout.podium.record', 'Record')}
            </dt>
            <dd className="font-semibold text-foreground">
              {data.wins}-{data.losses}
            </dd>
          </div>
          {showWinRate && typeof data.winRate === 'number' && (
            <div className="flex items-center justify-between">
              <dt className="text-muted-foreground uppercase tracking-wide">
                {t('knockout.podium.winRate', 'Win rate')}
              </dt>
              <dd className="font-semibold text-foreground">{data.winRate}%</dd>
            </div>
          )}
          {typeof data.extraStats?.setDifference === 'number' && (
            <div className="flex items-center justify-between">
              <dt className="text-muted-foreground uppercase tracking-wide">
                {t('knockout.podium.setDifference', 'Set difference')}
              </dt>
              <dd className="font-semibold text-foreground">
                {data.extraStats.setDifference > 0 ? '+' : ''}
                {data.extraStats.setDifference}
              </dd>
            </div>
          )}
          {typeof data.extraStats?.eloRating === 'number' && (
            <div className="flex items-center justify-between">
              <dt className="text-muted-foreground uppercase tracking-wide">
                {t('knockout.podium.eloRating', 'ELO rating')}
              </dt>
              <dd className="font-semibold text-foreground">{Math.round(data.extraStats.eloRating)}</dd>
            </div>
          )}
        </dl>
      </div>
    </div>
  )
}

function formatPlayerNames(players: Player[]) {
  if (players.length === 0) {
    return ''
  }

  const names = players.map(player => player.displayName)
  if (names.length === 1) {
    return names[0]
  }

  // Prefer localized list formatting when available
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return new (Intl as any).ListFormat(undefined, { style: 'long', type: 'conjunction' }).format(names)
  } catch (_error) {
    if (names.length === 2) {
      return names.join(' & ')
    }
    return names.join(', ')
  }
}
